import React, { useState } from "react";
import { useAuth, API } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";

const EXAMPLE_QUERIES = [
  "Which regions have the worst air quality this week?",
  "Are there any cities approaching dangerous PM2.5 levels?",
  "What's the global pollution trend compared to last week?",
  "Which industries are most at risk from current conditions?",
];

function TierGate({ requiredTier, currentTier, children }) {
  const navigate = useNavigate();
  const tierOrder = ["free", "basic", "pro", "enterprise"];
  const hasAccess = tierOrder.indexOf(currentTier) >= tierOrder.indexOf(requiredTier);
  if (hasAccess) return children;
  return (
    <div style={{
      padding: 32, textAlign: "center",
      border: "1px dashed var(--border2)", borderRadius: "var(--radius)",
      background: "rgba(0,229,170,0.02)"
    }}>
      <div style={{ fontSize: 28, marginBottom: 12 }}>🔒</div>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>
        {requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1)} plan required
      </div>
      <div style={{ fontSize: 12, color: "var(--subtext)", marginBottom: 20 }}>
        Upgrade to unlock AI-powered {requiredTier === "pro" ? "intelligence features" : "access"}
      </div>
      <button className="btn btn-primary" onClick={() => navigate("/pricing")}>
        View Plans
      </button>
    </div>
  );
}

export default function AIIntelligence() {
  const { user } = useAuth();
  const tier = user?.tier || "free";

  const [query, setQuery] = useState("");
  const [nlqResult, setNlqResult] = useState(null);
  const [nlqLoading, setNlqLoading] = useState(false);
  const [nlqError, setNlqError] = useState("");

  const [anomalies, setAnomalies] = useState(null);
  const [anomalyLoading, setAnomalyLoading] = useState(false);

  const handleNLQ = async (q = query) => {
    if (!q.trim()) return;
    setNlqLoading(true); setNlqError(""); setNlqResult(null);
    try {
      const r = await API.post("/api/ai/ask", { query: q });
      setNlqResult(r.data);
    } catch (e) {
      setNlqError(e.response?.data?.detail || "Query failed");
    } finally {
      setNlqLoading(false);
    }
  };

  const handleFetchAnomalies = async () => {
    setAnomalyLoading(true);
    try {
      const r = await API.get("/api/ai/anomalies?parameter=pm25&explain=true");
      setAnomalies(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setAnomalyLoading(false);
    }
  };

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
        <h1 className="page-title">AI Intelligence</h1>
        <span className={`tier-chip tier-${tier}`}>{tier}</span>
      </div>
      <p className="page-sub">Claude-powered environmental data analysis · Anomaly detection · Predictions · Natural language querying</p>

      {/* ── Natural Language Query ────────────────────────────────────── */}
      <div className="card ai-glow" style={{ marginBottom: 16 }}>
        <div className="card-title">Ask the data anything</div>

        <TierGate requiredTier="basic" currentTier={tier}>
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <input
              className="input"
              placeholder="e.g. Which cities have dangerous air quality right now?"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleNLQ()}
            />
            <button
              className="btn btn-primary"
              onClick={() => handleNLQ()}
              disabled={nlqLoading || !query.trim()}
              style={{ flexShrink: 0 }}
            >
              {nlqLoading ? "Thinking..." : "Ask"}
            </button>
          </div>

          {/* Example queries */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {EXAMPLE_QUERIES.map((q, i) => (
              <button key={i} onClick={() => { setQuery(q); handleNLQ(q); }} style={{
                padding: "4px 12px", borderRadius: 6,
                background: "var(--surface2)", border: "1px solid var(--border)",
                fontSize: 11, color: "var(--subtext)", cursor: "pointer",
                transition: "all 0.15s",
              }}
                onMouseOver={e => e.target.style.borderColor = "var(--accent)"}
                onMouseOut={e => e.target.style.borderColor = "var(--border)"}
              >
                {q}
              </button>
            ))}
          </div>

          {nlqError && (
            <div style={{ padding: "10px 14px", borderRadius: 7, background: "rgba(255,59,59,0.08)", color: "var(--danger)", fontSize: 12, marginBottom: 12 }}>
              {nlqError}
            </div>
          )}

          {nlqResult && (
            <div style={{
              padding: 20, borderRadius: 8,
              background: "var(--surface2)", border: "1px solid var(--border2)",
            }}>
              <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--accent)", marginBottom: 10, letterSpacing: "0.1em" }}>
                AI RESPONSE · {new Date(nlqResult.generated_at).toLocaleTimeString()}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                {nlqResult.response}
              </div>
            </div>
          )}
        </TierGate>
      </div>

      {/* ── Anomaly Detection ─────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Anomaly Detection · PM2.5</div>
          <TierGate requiredTier="pro" currentTier={tier}>
            <button className="btn btn-outline" onClick={handleFetchAnomalies} disabled={anomalyLoading} style={{ fontSize: 12, padding: "6px 14px" }}>
              {anomalyLoading ? "Scanning..." : "Run Scan"}
            </button>
          </TierGate>
        </div>

        <TierGate requiredTier="pro" currentTier={tier}>
          {!anomalies && !anomalyLoading && (
            <div style={{ color: "var(--subtext)", fontSize: 12, padding: "16px 0" }}>
              Click "Run Scan" to detect statistical anomalies in current global PM2.5 readings.
            </div>
          )}
          {anomalies && (
            <div>
              <div style={{ fontSize: 12, color: "var(--subtext)", marginBottom: 16 }}>
                Scanned {anomalies.total_readings} readings · Found {anomalies.anomalies_found} anomalies
              </div>
              {anomalies.anomalies.slice(0, 6).map((a, i) => {
                const severityColor = { critical: "var(--danger)", high: "var(--warning)", medium: "var(--accent2)" }[a.severity] || "var(--subtext)";
                return (
                  <div key={i} style={{
                    padding: "14px 0", borderBottom: "1px solid var(--border)",
                    display: "grid", gridTemplateColumns: "1fr auto",
                  }}>
                    <div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: severityColor, fontWeight: 700 }}>
                          {a.severity?.toUpperCase()}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{a.city || a.location || "Unknown"}</span>
                        <span style={{ fontSize: 11, color: "var(--subtext)" }}>{a.country}</span>
                      </div>
                      <div style={{ fontSize: 12, color: "var(--subtext)", marginBottom: a.ai_explanation ? 8 : 0 }}>
                        Value: <span style={{ color: severityColor }}>{a.value} µg/m³</span> · Expected: {a.expected_value} µg/m³ · Deviation: +{a.deviation_pct}%
                      </div>
                      {a.ai_explanation && (
                        <div style={{
                          fontSize: 11, color: "var(--text)", lineHeight: 1.7,
                          padding: "8px 12px", background: "var(--surface2)",
                          borderRadius: 6, borderLeft: `2px solid ${severityColor}`
                        }}>
                          {a.ai_explanation}
                        </div>
                      )}
                    </div>
                    <div style={{ fontFamily: "var(--mono)", fontSize: 13, color: severityColor, fontWeight: 700, paddingLeft: 16, alignSelf: "flex-start", paddingTop: 2 }}>
                      z={a.z_score}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TierGate>
      </div>

      {/* ── Feature Preview for lower tiers ──────────────────────────── */}
      {["free", "basic"].includes(tier) && (
        <div className="card" style={{ background: "rgba(0,229,170,0.02)", border: "1px solid rgba(0,229,170,0.12)" }}>
          <div className="card-title">Pro AI Features Preview</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              { icon: "◈", title: "Predictive Alerts", desc: "Forecast threshold breaches 24-48h in advance using trend analysis + AI" },
              { icon: "◉", title: "Anomaly Detection", desc: "Statistical + AI-explained outliers across all global sensor data" },
              { icon: "▦", title: "Weekly Narratives", desc: "Claude-written intelligence briefings tailored for your sector" },
              { icon: "⬡", title: "Unlimited NLQ", desc: "Ask anything about global environmental conditions in plain English" },
            ].map((f, i) => (
              <div key={i} style={{ padding: "14px", background: "var(--surface2)", borderRadius: 8 }}>
                <div style={{ fontSize: 18, marginBottom: 6, color: "var(--accent)" }}>{f.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{f.title}</div>
                <div style={{ fontSize: 11, color: "var(--subtext)", lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <a href="/pricing" className="btn btn-primary" style={{ fontSize: 12 }}>Upgrade to Pro →</a>
          </div>
        </div>
      )}
    </div>
  );
}
